import { delay } from 'redux-saga';
import {
  call,
  all,
  put,
  take,
  race,
  select,
  getContext,
  takeLatest,
  takeEvery,
} from 'redux-saga/effects';
import { push } from 'connected-react-router';

import { Logger } from 'utils/Logger';
import { isUrl } from 'utils/Validators';
import { NOTIFICATIONS_POLLING_DELAY_IN_MS } from 'constants';
import { makeSelectPolling } from './selectors';

import {
  REQUEST_MARK_AS_SEEN,
  REQUEST_MARK_AS_READ,
  REQUEST_NOTIFICATIONS,
  START_NOTIFICATION_POLLING,
  STOP_NOTIFICATION_POLLING,
} from './types';

import {
  startedNotificationPolling,
  requestNotificationsSuccess,
  requestNotificationsFailure,
  notificationPollingSuccess,
} from './reducer';

const logger = new Logger('Saga:Notifications');

/**
 * @name requestNotifications
 * @description
 * Method that requests a list of notification from the API.
 * If a page is provided it will request that particular page
 * @param {number} page Page requested, if undefined will default to `1`.
 */
export function* getNotifications(page = 1) {
  const authApiService = yield getContext('authApiService');
  const routerService = yield getContext('routerService');

  const response = yield authApiService.get(routerService.generate('api.notifications', { page }));

  return { ...response.data };
}

export function* requestNotificationsSaga(action = {}) {
  try {
    const response = yield call(getNotifications, action.page);

    yield put(requestNotificationsSuccess(response, action.page));
  } catch (error) {
    yield put(requestNotificationsFailure(error));
  }
}

export function* markNotificationsAsSeenSaga(action) {
  const authApiService = yield getContext('authApiService');
  const routerService = yield getContext('routerService');

  try {
    yield authApiService.post(routerService.generate('api.notifications_mark_as_seen'), {
      ids: action.notificationIds,
    });
  } catch (error) {
    logger.error('Failed to mark notifications as seen with the following error', error);
  }
}

export function* markNotificationAsReadSaga(action) {
  const authApiService = yield getContext('authApiService');
  const routerService = yield getContext('routerService');

  try {
    yield authApiService.post(
      routerService.generate('api.notification_mark_as_read', {
        notificationId: action.notification.id,
      }),
    );

    const url = action.notification.action;

    if (isUrl(url)) {
      window.location.href = url;
    } else {
      const cleanUrl = url.replace(/^\/app/, '');

      yield put(push(cleanUrl));
    }
  } catch (error) {
    logger.error('Failed to mark notification as read with the following error', error);
  }
}

export function* notificationPolling() {
  while (true) {
    const response = yield getNotifications();

    yield put(notificationPollingSuccess(response));
    yield call(delay, NOTIFICATIONS_POLLING_DELAY_IN_MS);
  }
}

export function* startPolling() {
  const isPolling = yield select(makeSelectPolling());

  // If already polling then we do nothing
  if (isPolling) {
    return;
  }

  logger.info('Starting notifications polling');
  yield put(startedNotificationPolling());

  try {
    const { canceled } = yield race({
      polling: call(notificationPolling),
      canceled: take(STOP_NOTIFICATION_POLLING),
    });

    logger.info(`Stopped polling notifications. Reason ${canceled ? 'CANCELED' : 'ERROR'}`);
  } catch {
    logger.info('Stopped polling notifications. Reason ERROR');
  }
}

export default function* notificationsSaga() {
  return yield all([
    takeLatest(REQUEST_NOTIFICATIONS, requestNotificationsSaga),
    takeEvery(START_NOTIFICATION_POLLING, startPolling),
    takeEvery(REQUEST_MARK_AS_SEEN, markNotificationsAsSeenSaga),
    takeEvery(REQUEST_MARK_AS_READ, markNotificationAsReadSaga),
  ]);
}
